// packages
import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';

import { colors, useToggle } from '../../../core';
import { SvgContainer } from '../../styles';
// svg components
import Logo from '../Logo';
import { UserProps } from '../UserDropdown';
// data 1
import {
  aboutUsLinks,
  doctorsLinks,
  patientsLinks,
  prenuvoScanLinks,
  resourcesLinks,
} from './data';
// data 2
import {
  ABOUT_US_PAGE_SLUG,
  BOOK_AN_APPOINTMENT_PAGE_SLUG,
  CONTACT_PAGE_SLUG,
  EMPLOYERS_PAGE_SLUG,
  FOR_DOCTORS_PAGE_SLUG,
  FOR_PATIENTS_PAGE_SLUG,
  HOME_PAGE_SLUG,
  LOCATIONS_PAGE_SLUG,
  PRENUVO_SCAN_PAGE_SLUG,
  PRICING_PAGE_SLUG,
  RESOURCES_PAGE_SLUG,
  SIGN_IN_SLUG,
  TAKE_A_RISK_TEST_PAGE_SLUG,
} from './navLinks';
// components
import NavLinkWithSubMenu from './NavLinkWithSubMenu';
import { NavLink } from './NavLinkWithSubMenu/styles';
// styles
import {
  HamburgerMenu,
  HamburgerMenuLine,
  MobileSignInLink,
  NavBottom,
  NavBottomInner,
  NavDivider,
  NavMenuBottomLeft,
  NavMenuBottomRight,
  NavMenuTopRight,
  NavMobile,
  NavMobileMenu,
  NavTop,
  NavTopInner,
  NavWrapper,
  Space,
} from './styles';
import SignIn from './svg/SignIn';

// props
interface NavProps {
  showNavMenu?: boolean;
  user?: UserProps | undefined;
  location?: any; // WindowLocation from @reach/router;
  urlToLogin?: string;
}

interface ScreenReaderTextProps {
  children: React.ReactNode;
}

const ScreenReaderTextStyles = styled.span`
  &:not(:focus):not(:active) {
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }
`;

const ScreenReaderText = ({ children }: ScreenReaderTextProps) => (
  <ScreenReaderTextStyles>{children}</ScreenReaderTextStyles>
);

const LoadableUserDropdown = lazy(() => import('../UserDropdown'));

const Nav = ({ showNavMenu = true, user, location, urlToLogin = SIGN_IN_SLUG }: NavProps) => {
  const ref = useRef<any>(null);
  const [activeIndex, setActiveIndex] = useState(null);

  const theme = useTheme();

  const toggleSubMenu = (index: any) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const [toggle, setToggle] = useToggle();

  const navClickHandler = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    slug: string
  ) => {
    const { pathname } = location;

    if (pathname === slug) {
      event.preventDefault();
      setToggle();
    }
  };

  const [isNavOpen, setNavOpen] = useState(false);

  const handleNavAnchorClick = useCallback(() => {
    setNavOpen(!isNavOpen);
  }, [isNavOpen]);

  const handleEscape = useCallback((keyboardEvent) => {
    if (keyboardEvent.key === 'Escape') {
      setNavOpen(false);
    }
  }, []);

  const handleClickOutside = useCallback((clickEvent) => {
    if (ref && ref.current && !ref.current.contains(clickEvent.target)) {
      setNavOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keyup', handleEscape);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keyup', handleEscape);
    };
  }, [handleEscape, handleClickOutside]);

  const getInitials = () => {
    let initials = '';
    if (user?.firstname?.length) {
      initials += user.firstname[0];
    }
    if (user?.lastname?.length) {
      initials += user.lastname[0];
    }
    return initials;
  };

  return (
    <NavWrapper menuIsOpen={toggle} ref={ref}>
      {/* mobile menu */}
      <NavMobile>
        <NavMobileMenu>
          <li>
            {showNavMenu ? (
              <HamburgerMenu menuIsOpen={toggle} onClick={setToggle} type="button">
                <HamburgerMenuLine />
                <HamburgerMenuLine />
                <ScreenReaderText>Open Mobile Menu</ScreenReaderText>
              </HamburgerMenu>
            ) : (
              <Space />
            )}
          </li>

          <li>
            <a href={HOME_PAGE_SLUG} style={{ padding: '0' }}>
              <SvgContainer widthInPx={152}>
                <Logo />
              </SvgContainer>
              <ScreenReaderText>Go to Home Page</ScreenReaderText>
            </a>
          </li>

          <li>
            {user ? (
              <Suspense fallback={null}>
                <LoadableUserDropdown
                  user={{ ...user, initials: getInitials() }}
                  handleNavAnchorClick={handleNavAnchorClick}
                  isNavOpen={isNavOpen}
                />
              </Suspense>
            ) : (
              <MobileSignInLink href={urlToLogin} rel="noopener noreferrer">
                Sign In
              </MobileSignInLink>
            )}
          </li>
        </NavMobileMenu>
      </NavMobile>

      <NavTop>
        <NavTopInner>
          {/* left box - logo */}
          <a href={HOME_PAGE_SLUG} style={{ padding: '0' }}>
            <Logo />

            <ScreenReaderText>Go to Home Page</ScreenReaderText>
          </a>

          {/* right box - top right menu */}
          <NavMenuTopRight>
            <NavLink addMargin>
              <a href="tel:18337736886">+1 833 773 6886</a>
            </NavLink>

            <NavLink addMargin>
              <a href={CONTACT_PAGE_SLUG}>Contact</a>
            </NavLink>

            <NavLink addMargin>
              <NavDivider color={theme.navBorderColor || colors.edgewater} />
            </NavLink>

            <NavLink mode="flex">
              {user ? (
                <Suspense fallback={null}>
                  <LoadableUserDropdown
                    user={user}
                    handleNavAnchorClick={handleNavAnchorClick}
                    isNavOpen={isNavOpen}
                  />
                </Suspense>
              ) : (
                <a href={urlToLogin} rel="noopener noreferrer">
                  <span>Sign in</span>
                  <SvgContainer widthInPx={30}>
                    <SignIn
                      fillColor={theme.navUserIconColor}
                      backgroundColor={theme.navUserIconBackgroundColor}
                    />
                  </SvgContainer>
                </a>
              )}
            </NavLink>
          </NavMenuTopRight>
        </NavTopInner>
      </NavTop>

      {showNavMenu ? (
        <NavBottom menuIsOpen={toggle}>
          <NavBottomInner>
            <NavMenuBottomLeft>
              <NavLinkWithSubMenu
                activeIndex={activeIndex}
                addMargin
                index={0}
                navClickHandler={navClickHandler}
                subMenuLinks={prenuvoScanLinks}
                toggleFn={toggleSubMenu}
                topLevelLinkText="Prenuvo Scans"
                topLevelSlug={PRENUVO_SCAN_PAGE_SLUG}
              />

              <NavLinkWithSubMenu
                activeIndex={activeIndex}
                addMargin
                index={1}
                navClickHandler={navClickHandler}
                subMenuLinks={aboutUsLinks}
                toggleFn={toggleSubMenu}
                topLevelLinkText="About Us"
                topLevelSlug={ABOUT_US_PAGE_SLUG}
              />

              <NavLinkWithSubMenu
                activeIndex={activeIndex}
                addMargin
                index={2}
                navClickHandler={navClickHandler}
                subMenuLinks={patientsLinks}
                toggleFn={toggleSubMenu}
                topLevelLinkText="For Patients"
                topLevelSlug={FOR_PATIENTS_PAGE_SLUG}
              />

              <NavLinkWithSubMenu
                activeIndex={activeIndex}
                addMargin
                index={3}
                navClickHandler={navClickHandler}
                subMenuLinks={doctorsLinks}
                toggleFn={toggleSubMenu}
                topLevelLinkText="For Doctors"
                topLevelSlug={FOR_DOCTORS_PAGE_SLUG}
              />

              <NavLink addMargin>
                <a
                  onClick={(event: any) => navClickHandler(event, EMPLOYERS_PAGE_SLUG)}
                  href={EMPLOYERS_PAGE_SLUG}
                >
                  Employers
                </a>
              </NavLink>

              <NavLinkWithSubMenu
                activeIndex={activeIndex}
                addMargin
                index={4}
                navClickHandler={navClickHandler}
                subMenuLinks={resourcesLinks}
                toggleFn={toggleSubMenu}
                topLevelLinkText="Resources"
                topLevelSlug={RESOURCES_PAGE_SLUG}
              />

              <NavLink addMargin>
                <a
                  onClick={(event: any) => navClickHandler(event, PRICING_PAGE_SLUG)}
                  href={PRICING_PAGE_SLUG}
                >
                  Pricing
                </a>
              </NavLink>

              <NavLink addMargin>
                <a
                  onClick={(event: any) => navClickHandler(event, LOCATIONS_PAGE_SLUG)}
                  href={LOCATIONS_PAGE_SLUG}
                >
                  Locations
                </a>
              </NavLink>
            </NavMenuBottomLeft>

            <NavMenuBottomRight>
              <NavLink addMargin mode="light">
                <a href={TAKE_A_RISK_TEST_PAGE_SLUG}>Take a Risk Test</a>
              </NavLink>

              <NavLink addMargin>
                <NavDivider color={theme.navBorderColor || colors.edgewater} />
              </NavLink>

              <NavLink mode="light">
                <a href={BOOK_AN_APPOINTMENT_PAGE_SLUG}>Book an Appointment</a>
              </NavLink>
            </NavMenuBottomRight>
          </NavBottomInner>
        </NavBottom>
      ) : null}
    </NavWrapper>
  );
};

export default Nav;
